import React, { useRef, useEffect, useState, useCallback } from 'react';
import styled from 'styled-components';
import { useInView } from 'react-intersection-observer';

// Import assets
import Bollywood from '../../Assets/testimonial/BollywoodStudio.webp';
import starImg from '../../Assets/testimonial/star.webp';
import Dhiyoyo from '../../Assets/testimonial/DhiyoyoStudio.webp';
import Ashok from '../../Assets/testimonial/AshokPhotography.webp';
import twooo from '../../Assets/testimonial/twooo.webp';
import oneee from '../../Assets/testimonial/oneee.webp';
import user1 from '../../Assets/testimonial/twooo.webp';
import user2 from '../../Assets/testimonial/BollywoodStudio.webp';
import user3 from '../../Assets/testimonial/DhiyoyoStudio.webp';
import user4 from '../../Assets/testimonial/oneee.webp';
import user5 from '../../Assets/testimonial/AshokPhotography.webp';

// How many cards are visible at once:
// >=1280px (xl/2xl) -> 3 cards, >=1024px (lg) -> 2 cards, below -> 1 card
const getSlidesToShow = () => {
  if (typeof window === 'undefined') return 1;
  const w = window.innerWidth;
  if (w >= 1280) return 3;
  if (w >= 1024) return 2;
  return 1;
};

// Gap between cards, matched exactly between JS math and rendered CSS
// (both come from this single function — no separate CSS media-query gap).
const getCardGap = () => {
  if (typeof window === 'undefined') return 24;
  const w = window.innerWidth;
  if (w >= 1600) return 32;
  if (w >= 1280) return 28;
  return 24;
};

const NewTestimonialSection = () => {
  const { ref: sectionRef, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const reviews = [
    {
      name: 'Bollywood Studio',
      avatar: Bollywood,
      text: `KwikMemories has completely transformed how we run our studio operations. What impressed me the most is how smooth the AI face scan works even in large group shoots with hundreds of faces, it sorts everything perfectly without a single error with 99.9% of accuracy. Running everything from the mobile app is so convenient — bookings, galleries, payments, all in one tap. The quality of the platform itself reflects in how our clients perceive our brand. Highly recommend it to every serious studio owner.`,
      rating: ' Praskash Shinde, Founder - Surat',
    },
    {
      name: 'Dhiyoyo Studio',
      avatar: Dhiyoyo,
      text: `I've tried multiple platforms before, but nothing came close to KwikMemories. The AI face scan feature is honestly a game-changer — it identifies and organizes client photos so accurately that our delivery time has been cut in half. Managing everything through the mobile app means I don't even need to sit at a desk anymore. The quality KwikMemories delivers to our clients is simply unmatchable. Our clients now feel like they're getting a luxury experience.`,
      rating: 'Bhavdip Desai, CEO - Navasari',
    },
    {
      name: ' JV Studio',
      avatar: oneee,
      text: `Honestly, we were skeptical at first but KwikMemories proved us wrong from day one. The AI face scan feature is the smoothest we've ever seen. Clients get their photos delivered in a way that feels premium and personal. The mobile app is so well-designed that our entire team manages Easily. The quality KwikMemories brings to every delivery is on another level, our clients keep complimenting us on how professional everything looks.`,
      rating: 'Surendra Singh Deora, Owner - Surat',
    },
    {
      name: 'Vikalp Photography',
      avatar: twooo,
      text: `We've been using KwikMemories for a while now and the experience has been nothing short of excellent. The AI face scan feature is the smoothest and most accurate tool we've used it handles even the most complex event galleries effortlessly. The mobile app keeps our entire workflow in our hands, literally. And the quality KwikMemories delivers from gallery presentation to client access is truly unmatchable and the uses of two separate access like Guest and Private it makes the process smooth for public gathering event as well. Our clients notice the difference and that directly reflects in our referrals and repeat bookings.`,
      rating: 'Vikalp Photography, Studio Director - Surat',
    },
    {
      name: 'Ashok Photography',
      avatar: Ashok,
      text: `Before KwikMemories, I was juggling 4-5 different apps just to run my studio and still things were falling through the cracks. Now everything lives in one place. Bookings, payments, client galleries, Team management, Lead Generation, etc — I manage my entire workflow from KwikMemories without switching a single tab. The time I've saved is unreal. If you're a solo photographer who wants to run a professional studio without wasting half your day on admin side — this is the only platform you need."`,
      rating: 'Ashok Goswami, Founder - Rajasthan',
    },
  ];

  const total = reviews.length;

  /* ────────────────────────────────────────────────────────────────────────
     ANIMATED CAROUSEL — 3 cards on xl/2xl, 2 on lg, 1 below.

     Both cardWidth AND cardGap are measured/derived in JS on every resize
     and applied via inline styles (not CSS media queries) — this is what
     guarantees the math always matches what's rendered, so cards are never
     cut off at the viewport edge.
  ──────────────────────────────────────────────────────────────────────── */
  const [activeIndex, setActiveIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(true);
  const [slidesToShow, setSlidesToShow] = useState(getSlidesToShow);
  const [cardGap, setCardGap] = useState(getCardGap);
  const [cardWidth, setCardWidth] = useState(0);

  const autoplayRef = useRef(null);
  const viewportRef = useRef(null);

  const trackIndex = activeIndex + slidesToShow;

  const goToNext = () => setActiveIndex((i) => i + 1);
  const goToPrev = () => setActiveIndex((i) => i - 1);

  const restartAutoplay = () => {
    if (autoplayRef.current) window.clearInterval(autoplayRef.current);
    autoplayRef.current = window.setInterval(goToNext, 3500);
  };

  const handleNextClick = () => {
    goToNext();
    restartAutoplay();
  };

  const handlePrevClick = () => {
    goToPrev();
    restartAutoplay();
  };

  // Measure viewport and compute exact per-card pixel width + gap
  const measure = useCallback(() => {
    const shown = getSlidesToShow();
    const gap = getCardGap();
    setSlidesToShow(shown);
    setCardGap(gap);
    if (viewportRef.current) {
      const viewportWidth = viewportRef.current.offsetWidth;
      const width = (viewportWidth - gap * (shown - 1)) / shown;
      setCardWidth(width);
    }
  }, []);

  useEffect(() => {
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [measure]);

  // Autoplay
  useEffect(() => {
    restartAutoplay();
    return () => window.clearInterval(autoplayRef.current);
  }, []);

  // Seamless wrap-around once a transition onto a clone finishes
  useEffect(() => {
    if (activeIndex === total) {
      const t = window.setTimeout(() => {
        setIsTransitioning(false);
        setActiveIndex(0);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => setIsTransitioning(true));
        });
      }, 650);
      return () => window.clearTimeout(t);
    }
    if (activeIndex === -1) {
      const t = window.setTimeout(() => {
        setIsTransitioning(false);
        setActiveIndex(total - 1);
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => setIsTransitioning(true));
        });
      }, 650);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [activeIndex, total]);

  // Clone `slidesToShow` cards on each side for a seamless multi-card loop
  const carouselSlides = [
    ...reviews.slice(total - slidesToShow),
    ...reviews,
    ...reviews.slice(0, slidesToShow),
  ];

  const step = cardWidth + cardGap;
  const trackWidth = carouselSlides.length * cardWidth + (carouselSlides.length - 1) * cardGap;
  const translateX = -(trackIndex * step);

  return (
    <SectionWrapper ref={sectionRef} id="testimonials">
      <CardsScene>
        <SectionHeader $inView={inView}>
          <Title>
            Clients says about <br />
            <TitleGradient>Our service</TitleGradient>
          </Title>
          <Subtitle>TESTIMONIAL</Subtitle>
        </SectionHeader>

        <CardsStage>
  <CarouselStackArea>
    <CardsBackdrop>
      <BackdropUsersRow>
        <BackdropUserAvatar src={user1} alt="User" />
        <BackdropUserAvatar src={user2} alt="User" />
        <BackdropUserAvatar src={user3} alt="User" />
        <BackdropUserAvatar src={user4} alt="User" />
        <BackdropUserAvatar src={user5} alt="User" />
      </BackdropUsersRow>
      <BackdropScore>4.9</BackdropScore>
      <BackdropRight>
        <BackdropStarsRow>
          <BackdropStar src={starImg} alt="Star rating" />
          <BackdropStar src={starImg} alt="Star rating" />
          <BackdropStar src={starImg} alt="Star rating" />
          <BackdropStar src={starImg} alt="Star rating" />
          <BackdropStar src={starImg} alt="Star rating" />
        </BackdropStarsRow>
        <BackdropRatedText>
          <strong>4.9/5.0</strong> Rated by 7,000 User
        </BackdropRatedText>
      </BackdropRight>
    </CardsBackdrop>

    <CarouselArrows>
      <CarouselArrow type="button" aria-label="Previous testimonial" onClick={handlePrevClick}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </CarouselArrow>
      <CarouselArrow type="button" aria-label="Next testimonial" onClick={handleNextClick}>
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </CarouselArrow>
    </CarouselArrows>

    <CarouselWrapper>
      <CarouselViewport ref={viewportRef}>
        {cardWidth > 0 && (
          <CarouselTrack
            style={{
              width: `${trackWidth}px`,
              gap: `${cardGap}px`,
              transform: `translateX(${translateX}px)`,
              transition: isTransitioning ? 'transform 0.6s ease-in-out' : 'none',
            }}
          >
            {carouselSlides.map((rev, index) => (
              <CarouselCard key={`${rev.name}-${index}`} style={{ width: `${cardWidth}px` }}>
                <CardHeader>
                  <Avatar src={rev.avatar} alt={rev.name} />
                  <UserInfo>
                    <UserName>{rev.name}</UserName>
                  </UserInfo>
                </CardHeader>
                <ReviewText>"{rev.text}"</ReviewText>
                <RatingSection>
                  <StarsRow>
                    <StarIcon src={starImg} alt="Star rating" />
                    <StarIcon src={starImg} alt="Star rating" />
                    <StarIcon src={starImg} alt="Star rating" />
                    <StarIcon src={starImg} alt="Star rating" />
                    <StarIcon src={starImg} alt="Star rating" />
                  </StarsRow>
                  <RatingVal>{rev.rating}</RatingVal>
                </RatingSection>
              </CarouselCard>
            ))}
          </CarouselTrack>
        )}
      </CarouselViewport>
    </CarouselWrapper>
  </CarouselStackArea>

  <CarouselDots>
    {reviews.map((_, index) => (
      <CarouselDot
        key={index}
        type="button"
        aria-label={`Go to testimonial ${index + 1}`}
        $active={index === ((activeIndex % total) + total) % total}
        onClick={() => {
          setActiveIndex(index);
          restartAutoplay();
        }}
      />
    ))}
  </CarouselDots>
</CardsStage>
      </CardsScene>
    </SectionWrapper>
  );
};

export default NewTestimonialSection;

const BackdropUsersRow = styled.div`
  display: flex;
  align-items: center;
`;

const BackdropUserAvatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: 2px solid #fff;
  object-fit: cover;
  margin-left: -12px;

  &:first-child {
    margin-left: 0;
  }
`;

const SectionWrapper = styled.section`
  padding: 0 64px 20px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  overflow: visible;

  @media (max-width: 1024px) {
    padding: 10px 24px 12px;
  }

  @media (max-width: 768px) {
    padding: 10px 12px 12px;
  }

  @media (max-width: 480px) {
    padding: 6px 12px 12px;
  }

  @media (min-width: 1600px) {
    padding: 60px 100px 80px;
  }
`;

const CardsScene = styled.div`
  width: 100%;
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CardsStage = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  overflow: visible;
  background: #ffffff;
  z-index: 2;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 20px;
  z-index: 2;

  @media (min-width: 969px) {
    margin-top: -48px;
  }

  @media (min-width: 1600px) {
    margin-bottom: 56px;
  }
`;

const Title = styled.h2`
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-weight: 700;
  font-size: 64px;
  color: #121212;
  margin: 0 0 16px;
  line-height: 1.2;

  @media (max-width: 1024px) {
    font-size: 48px;
  }

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 12px;
  }

  @media (max-width: 420px) {
    font-size: 24px;
  }

  @media (min-width: 1600px) {
    font-size: 80px;
  }
`;

const TitleGradient = styled.span`
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`;

const Subtitle = styled.div`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-size: 24px;
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  color: rgba(0, 0, 0, 1);

  @media (max-width: 768px) {
    font-size: 18px;
  }

  &::before, &::after {
    content: '•';
    background: linear-gradient(140.02deg, #FFC951 2.22%, #C94DAA 96.12%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    color: transparent;
  }

  @media (min-width: 1600px) {
    font-size: 30px;
  }
`;

const CarouselStackArea = styled.div`
  position: relative;
  width: 100%;
`;

const CardsBackdrop = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 28px;
  pointer-events: none;
  white-space: nowrap;

  @media (max-width: 968px) {
    display: none;
  }

  @media (max-width: 1200px) {
    gap: 20px;
  }

  @media (min-width: 1600px) {
    gap: 22px;
  }
`;

const BackdropScore = styled.div`
  font-family: 'Playfair Display', serif;
  font-style: italic;
  font-weight: 700;
  font-size: clamp(60px, 7vw, 110px);
  line-height: 1;
  padding-left: 10px;
  display: flex;
  align-items: center;
  background: linear-gradient(140.02deg, #ffc951 2.22%, #c94daa 96.12%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: transparent;
`;

const BackdropRight = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BackdropStarsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const BackdropStar = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;

  @media (min-width: 1600px) {
    width: 28px;
    height: 28px;
  }
`;

const BackdropRatedText = styled.p`
  margin: 0;
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-size: 18px;
  line-height: 1.1;
  color: #222;
  white-space: nowrap;

  strong {
    font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  }

  @media (min-width: 1600px) {
    font-size: 22px;
  }
`;

/* ─────────── Arrows — top right, above the card row ─────────── */

const CarouselArrows = styled.div`
  position: relative;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  width: 100%;
  margin: 0 auto 12px;
  padding: 0 24px;
  box-sizing: border-box;
`;

const CarouselArrow = styled.button`
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  text-align: center;
  border: none;
  background: linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%);
  color: #ffffff;
  font-size: 24px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(201, 77, 170, 0.22);
  transition: transform 0.15s ease, opacity 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    opacity: 0.92;
  }

  &:active {
    transform: translateY(0);
  }

  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 20px;
  }
`;

/* ─────────── Carousel track — 3 on xl/2xl, 2 on lg, 1 below ───────────
   No CSS media-query gap here anymore — gap is set inline from JS
   (see cardGap state) so it always matches the width calculation. */

const CarouselWrapper = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  margin: 0 auto;
  padding: 0 24px;
  box-sizing: border-box;
`;

const CarouselViewport = styled.div`
  width: 100%;
  overflow: hidden;
`;

const CarouselTrack = styled.div`
  display: flex;
`;

const ReviewCard = styled.div`
  background: linear-gradient(144.01deg, rgba(255, 245, 228, 1) 0%, rgba(250, 237, 247, 1) 100%);
  position: relative;
  isolation: isolate;
  border-radius: 32px;
  padding: 28px 30px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  min-height: 350px;  

  @media (max-width: 1200px) {
    padding: 20px 26px;
    border-radius: 24px;
    min-height: 370px;
  }

  @media (max-width: 968px) {
    padding: 20px 22px;
  }

  @media (min-width: 1600px) {
    padding: 30px 30px;
    border-radius: 40px;
    min-height: 450px;
  }
`;

const CarouselCard = styled(ReviewCard)`
  flex: 0 0 auto;
  min-height: 380px;

  @media (max-width: 1200px) {
    min-height: 400px;
  }

  @media (min-width: 1600px) {
    min-height: 500px;
  }
`;

const CarouselDots = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 20px;
`;

const CarouselDot = styled.button`
  width: ${props => (props.$active ? '22px' : '8px')};
  height: 8px;
  border-radius: 999px;
  border: none;
  padding: 0;
  cursor: pointer;
  background: ${props =>
    props.$active
      ? 'linear-gradient(144.01deg, #ffc951 0%, #c94daa 100%)'
      : 'rgba(0,0,0,0.15)'};
  transition: width 0.25s ease, background 0.25s ease;
`;

/* ─────────── Shared card content pieces ─────────── */

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 4px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(255, 255, 255, 1);

  @media (min-width: 1600px) {
    margin-bottom: 24px;
    padding-bottom: 24px;
    gap: 20px;
  }
`;

const Avatar = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ffffff;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.05);

  @media (max-width: 1200px) {
    width: 52px;
    height: 52px;
  }

  @media (min-width: 1600px) {
    width: 80px;
    height: 80px;
    border-width: 3px;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const UserName = styled.span`
  font-family: 'Playfair Display', serif;
  font-weight: 700;
  font-size: 20px;
  color: #121212;

  @media (max-width: 1200px) {
    font-size: 18px;
  }

  @media (min-width: 1600px) {
    font-size: 26px;
  }
`;

const ReviewText = styled.p`
  font-family: 'Gilroy-Medium', 'Poppins', sans-serif;
  font-weight: 400;
  font-size: 14px;
  color: rgba(0, 0, 0, 1);
  line-height: 145%;
  margin: 10 0 18px 0;
  flex-grow: 1;

  @media (max-width: 1200px) {
    font-size: 13px;
    margin-bottom: 10px;
  }

  @media (max-width: 1024px) {
    font-size: 12px;
    margin-bottom: 12px;
  }

  @media (min-width: 1600px) {
    font-size: 16px;
    margin-bottom: 24px;
  }
`;

const RatingSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
`;

const StarsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const StarIcon = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;

  @media (max-width: 1200px) {
    width: 18px;
    height: 18px;
  }

  @media (min-width: 1600px) {
    width: 26px;
    height: 26px;
  }
`;

const RatingVal = styled.span`
  font-family: 'Gilroy-SemiBold', 'Poppins', sans-serif;
  font-weight: 600;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0;
  text-transform: uppercase;
  color: rgba(0, 0, 0, 1);

  @media (min-width: 1600px) {
    font-size: 12px;
  }
`;